import axios from 'axios';
import type {
  ConfigEntryResponse,
  ConfigEntryRequest,
  ConfigCategoryItem,
  ConfigChangeItem,
  PageResponse,
} from '../types/configCenter';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Bearer 토큰 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const configCenterApi = {
  /**
   * GET /config-center — 설정 목록 (CONFIG_CENTER_READ)
   * @param params category, keyword, sensitiveOnly, page, size, sort
   */
  list: (params: {
    category?: string;
    keyword?: string;
    sensitiveOnly?: boolean;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<ConfigEntryResponse>> => {
    const sp = new URLSearchParams();
    if (params.category) sp.set('category', params.category);
    if (params.keyword) sp.set('keyword', params.keyword);
    if (params.sensitiveOnly !== undefined)
      sp.set('sensitiveOnly', String(params.sensitiveOnly));
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<ConfigEntryResponse> }>(
        `/config-center?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /**
   * GET /config-center/{key} — 단건 조회 (CONFIG_CENTER_READ)
   */
  get: (key: string): Promise<ConfigEntryResponse> =>
    apiClient
      .get<unknown, { data: ConfigEntryResponse }>(`/config-center/${encodeURIComponent(key)}`)
      .then((res) => res.data),

  /**
   * GET /config-center/categories — 카테고리 목록 (CONFIG_CENTER_READ)
   */
  categories: (): Promise<ConfigCategoryItem[]> =>
    apiClient
      .get<unknown, { data: ConfigCategoryItem[] }>('/config-center/categories')
      .then((res) => res.data),

  /**
   * GET /config-center/changes — 변경 피드 (CONFIG_CENTER_READ)
   */
  changes: (params: {
    configKey?: string;
    changeType?: string;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<ConfigChangeItem>> => {
    const sp = new URLSearchParams();
    if (params.configKey) sp.set('configKey', params.configKey);
    if (params.changeType) sp.set('changeType', params.changeType);
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<ConfigChangeItem> }>(
        `/config-center/changes?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /**
   * POST /config-center — 설정 생성 (CONFIG_CENTER_WRITE)
   * → 201 Created
   */
  create: (body: ConfigEntryRequest): Promise<ConfigEntryResponse> =>
    apiClient
      .post<unknown, { data: ConfigEntryResponse }>('/config-center', body)
      .then((res) => res.data),

  /**
   * PUT /config-center/{key} — 설정 수정 (CONFIG_CENTER_WRITE)
   * key in path + body (configKey 강제 주입)
   */
  update: (key: string, body: ConfigEntryRequest): Promise<ConfigEntryResponse> =>
    apiClient
      .put<unknown, { data: ConfigEntryResponse }>(
        `/config-center/${encodeURIComponent(key)}`,
        { ...body, configKey: key }
      )
      .then((res) => res.data),

  /**
   * DELETE /config-center/{key} — 설정 삭제 soft-delete (CONFIG_CENTER_WRITE)
   * → 204 No Content
   */
  remove: (key: string): Promise<void> =>
    apiClient
      .delete<unknown, { data: null }>(`/config-center/${encodeURIComponent(key)}`)
      .then(() => undefined),
};
